import { sortBy } from "lodash";
import { Deployment } from "../../schema/types/workload/deployment";
import { HealthCheckSpec, Workload } from "../../schema/types/workload/entity";
import { Options, OptionsMetric } from "../../schema/types/workload/workloadOptions";
import { linksOf, request } from "../../services/cpln";
import { parseLink } from "../../utils/linkParser/linkParser";

export interface WorkloadHealthObject {
  isReady: boolean;
  text: "Ready" | "Not Ready" | "Loading";
  isLatestReady: boolean;
  syncFailed: boolean;
  expectedDeploymentVersion: number;
  currentReadyVersion: number;
  hasIdentityIssue: boolean;
}

// Readiness values from the API are prefixed with sort order: "0_latestReady", "1_ready", "2_notReady"
export function parseReadiness(value?: string): string {
  if (!value) return "";
  const parts = value.split("_");
  return parts.length > 1 ? parts.slice(1).join("_") : value;
}

export const workloadHelpers = {
  getContainerByName(workload: any, name: string) {
    return workload.containers.find((c) => c.name === name);
  },
  getType(workload: any): string {
    return workload.spec.type;
  },
  getTypeText(workload: any) {
    const type = this.getType(workload);
    return type.slice(0, 1).toUpperCase() + type.slice(1);
  },
  getGvc(workload: any) {
    return workload.links
      .find((l) => l.rel === "gvc")!
      .href.split("/")
      .pop();
  },
  getDeploymentLink(workload: any) {
    return workload.links.find((l) => l.rel === "deployment")!.href;
  },
  getContainerNames(workload: any): string[] {
    return workload.spec.containers.map((c) => c.name);
  },
  getIsServerless(workload: any) {
    return workload.spec.type === "serverless";
  },
  getIsStandard(workload: any) {
    return workload.spec.type === "standard";
  },
  getIsCron(workload: any) {
    return workload.spec.type === "cron";
  },
  getIsStateful(workload: any) {
    return workload.spec.type === "stateful";
  },
  getHealth(deployments: Deployment[], workload: Workload): WorkloadHealthObject {
    // Readiness, syncFailed from workload.health (API-provided)
    const readiness = parseReadiness(workload.health?.readiness);
    const isReady = readiness === "latestReady" || readiness === "ready";
    const isLatestReady = readiness === "latestReady";
    const syncFailed = workload.health?.syncFailed === true;

    // These require deployment-level data
    let hasIdentityIssue = false;
    let expectedDeploymentVersion = 0;
    let currentReadyVersion = 0;

    if (deployments && Array.isArray(deployments) && deployments.length > 0) {
      const identityErrors = ["running with no identity", "is not allowed to reveal the secret"];
      expectedDeploymentVersion = deployments[0].status?.expectedDeploymentVersion || 0;

      for (const deployment of deployments) {
        if (!deployment.status?.ready) continue;

        for (const version of deployment.status.versions || []) {
          if (version.workload && version.ready && version.workload > currentReadyVersion) {
            currentReadyVersion = version.workload;
          }
          if (version.workload === deployment.status.expectedDeploymentVersion) {
            for (const errorMessage of identityErrors) {
              if (version.message?.includes(errorMessage)) {
                hasIdentityIssue = true;
              }
            }
          }
        }
      }
    }

    return {
      isReady,
      text: isReady ? "Ready" : "Not Ready",
      isLatestReady,
      syncFailed,
      expectedDeploymentVersion,
      currentReadyVersion,
      hasIdentityIssue,
    };
  },
  getReadyLocations(deployments: Deployment[]): string {
    let ready = 0;
    for (let deployment of deployments) {
      if (deployment.status?.ready) {
        ready += 1;
      }
    }
    return `${ready}/${deployments.length}`;
  },
  getReadyLocationsOnly(deployments: Deployment[]): number {
    const readyLocationsText = this.getReadyLocations(deployments);
    return Number(readyLocationsText.split("/")[0]);
  },
  getReadyReplicas(deployments: Deployment[], workload: Workload): string {
    let ready = 0;
    let total = 0;
    for (let deployment of deployments) {
      let latestVersion = -1;
      let deploymentReady = 0;
      let deploymentTotal = 0;

      if (workload.spec.type === "stateful") {
        for (let versionIndexStr in deployment.status?.versions || []) {
          const versionIndex = Number(versionIndexStr);
          const version = deployment.status!.versions![versionIndex];

          latestVersion = version.workload || 0;
          for (let containerIndexStr in Object.values(version.containers || {})) {
            const containerIndex = Number(containerIndexStr);
            if (containerIndex !== 0) {
              continue;
            }
            const container = Object.values(version.containers || {})[containerIndex];
            deploymentReady += container.resources?.replicasReady || 0;
            deploymentTotal += container.resources?.replicas || 0;
          }
        }
      } else {
        for (let versionIndexStr in deployment.status?.versions || []) {
          const versionIndex = Number(versionIndexStr);
          const version = deployment.status!.versions![versionIndex];
          if (version.workload || 0 > latestVersion) {
            latestVersion = version.workload || 0;
            for (let containerIndexStr in Object.values(version.containers || {})) {
              const containerIndex = Number(containerIndexStr);
              if (containerIndex !== 0) {
                continue;
              }
              const container = Object.values(version.containers || {})[containerIndex];
              deploymentReady = container.resources?.replicasReady || 0;
              deploymentTotal = container.resources?.replicas || 0;
            }
          }
        }
      }
      ready += deploymentReady;
      total += deploymentTotal;
    }
    return `${ready} / ${total}`;
  },
  getReadyReplicasOnly(deployments: Deployment[], workload: Workload): number {
    const readyReplicasText = this.getReadyReplicas(deployments, workload);
    return Number(readyReplicasText.split("/")[0]);
  },
  getTotalReplicas(deployments: Deployment[], workload: Workload): number {
    const readyReplicasText = this.getReadyReplicas(deployments, workload);
    return Number(readyReplicasText.split("/")[1]);
  },
  getContainers(workload: Workload) {
    return (workload.spec?.containers || []).map((c) => ({ name: c.name, image: c.image }));
  },
  getReadiness(deployments: Deployment[], workload: Workload) {
    const health = this.getHealth(deployments, workload);
    return health.text === "Loading" ? "Not Ready" : health.text;
  },
  getSuspendType(workload: any): "suspended" | "partiallySuspended" | "notSuspended" {
    let activeCount = 0;
    let suspendedCount = 0;

    if (workload.spec?.defaultOptions?.suspend) {
      suspendedCount += 1;
    } else {
      activeCount += 1;
    }

    for (let localOption of workload.spec?.localOptions || []) {
      if (localOption.suspend) {
        suspendedCount += 1;
      } else {
        activeCount += 1;
      }
    }

    if (suspendedCount > 0) {
      if (activeCount > 0) {
        return "partiallySuspended";
      } else {
        return "suspended";
      }
    } else {
      return "notSuspended";
    }
  },
  getCapacityAi(workload: Workload): boolean | undefined {
    if (!workload.spec?.defaultOptions) {
      return undefined;
    }
    return workload.spec.defaultOptions.capacityAI;
  },
  getMetric(options?: Options): OptionsMetric | "multi" {
    const m = options?.autoscaling?.metric;
    if (!options || !options.autoscaling || options.autoscaling.metric === "disabled") {
      return "disabled";
    }

    if (options!.autoscaling.multi && options!.autoscaling.multi.length > 0) {
      return "multi";
    }

    if (!m) {
      return "disabled";
    }

    return m;
  },
  getMetricText(workload: Workload, options?: Options): string {
    if (!options || !options.autoscaling || options.autoscaling.metric === "disabled") {
      return "Disabled";
    }
    const m = options!.autoscaling.metric;

    if (options!.autoscaling.multi && options!.autoscaling.multi.length > 0) {
      return "Multi (cpu, memory, rps)";
    }

    switch (m) {
      case "concurrency":
        return "Concurrency";
      case "cpu":
        return "CPU";
      case "keda":
        return "KEDA";
      case "latency":
        return "Latency";
      case "memory":
        return "Memory";
      case "rps":
        return "Requests Per Second (RPS)";
    }
    return "";
  },
  getMinScale(workload: Workload): number | undefined {
    if (!workload.spec?.defaultOptions?.autoscaling) {
      return undefined;
    }
    return workload.spec.defaultOptions.autoscaling.minScale;
  },
  getMaxScale(workload: Workload): number | undefined {
    if (!workload.spec?.defaultOptions?.autoscaling) {
      return undefined;
    }
    return workload.spec.defaultOptions.autoscaling.maxScale;
  },
  async getAsyncIsSuspended(link: string) {
    try {
      const { data } = await request<Workload>({ url: link });
      let hasActiveInstance = false;
      if (!data.spec?.defaultOptions) {
        hasActiveInstance = true;
      }
      if (data.spec?.defaultOptions && data.spec.defaultOptions.suspend === false) {
        hasActiveInstance = true;
      }
      if (data.spec!.localOptions && data.spec!.localOptions.find((l) => l.suspend === false)) {
        hasActiveInstance = true;
      }
      return !hasActiveInstance;
    } catch (e) {
      return false;
    }
  },
  // ---
  healthCheck: {
    getHasMethod(healthCheck: HealthCheckSpec) {
      let res = false;
      if (healthCheck.exec) res = true;
      if (healthCheck.tcpSocket) res = true;
      if (healthCheck.httpGet) res = true;
      if (healthCheck.grpc) res = true;
      return res;
    },
    getMethod(healthCheck: HealthCheckSpec) {
      let res = "command";
      if (healthCheck.exec) res = "command";
      if (healthCheck.tcpSocket) res = "tcp";
      if (healthCheck.httpGet) res = "http";
      if (healthCheck.grpc) res = "grpc";
      return res;
    },
  },
  firewall: {
    getInboundInternetReachable(firewall: any) {
      return firewall.inboundAllowCIDR.includes("0.0.0.0/0");
    },
    getOutboundInternetReachable(firewall: any) {
      return firewall.outboundAllowCIDR.includes("0.0.0.0/0");
    },
    getInboundAllowCIDRFiltered(firewall: any) {
      return firewall.inboundAllowCIDR.filter((i) => i !== "0.0.0.0/0");
    },
    getOutboundAllowCIDRFiltered(firewall: any) {
      return firewall.outboundAllowCIDR.filter((i) => i !== "0.0.0.0/0");
    },
  },
  async handleSuspend(links: string[]): Promise<true | string> {
    try {
      const promises: Promise<any>[] = [];
      for (const link of links) {
        const { data: workloadData } = await request<Workload>({
          url: link,
        });
        if (!workloadData.spec!.defaultOptions) {
          workloadData.spec!.defaultOptions = {};
        }
        workloadData.spec!.defaultOptions!.suspend = true;
        for (let localOption of workloadData.spec!.localOptions || []) {
          localOption.suspend = true;
        }
        promises.push(
          request({
            method: "patch",
            url: link,
            body: workloadData,
          }),
        );
      }
      await Promise.all(promises);
      return true;
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      return errorMessage;
    }
  },
  async handleUnsuspend(links: string[]): Promise<true | string> {
    try {
      const promises: Promise<any>[] = [];
      for (const link of links) {
        const { data: workloadData } = await request<Workload>({
          url: link,
        });
        if (!workloadData.spec!.defaultOptions) {
          workloadData.spec!.defaultOptions = {} as any;
        }
        workloadData.spec!.defaultOptions!.suspend = false;
        for (let localOption of workloadData.spec!.localOptions || []) {
          localOption.suspend = false;
        }
        promises.push(
          request({
            method: "patch",
            url: link,
            body: workloadData,
          }),
        );
      }
      await Promise.all(promises);
      return true;
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      return errorMessage;
    }
  },
  async handleForceRedeployment(links: string[]): Promise<true | string> {
    try {
      const promises: Promise<any>[] = [];
      for (const link of links) {
        const body = { tags: { "cpln/deployTimestamp": new Date().toISOString() } };
        promises.push(
          request({
            method: "patch",
            url: link,
            body: body,
          }),
        );
      }
      await Promise.all(promises);
      return true;
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      return errorMessage;
    }
  },
};

// by deployment name and status version, except for stateful workloads
export function sortDeployments(deployments: Deployment[]): Deployment[] {
  const res = sortBy(
    deployments.map((deployment) => {
      if (deployment.status?.versions) {
        deployment.status.versions = sortBy(deployment.status.versions, "workload").toReversed();
      }
      return deployment;
    }),
    "name",
  );
  return res;
}

export interface WorkloadBasicTableItem extends Workload {
  selfLink: string;
}

interface GetWorkloadBasicTableItemProps {
  link?: string;
  name?: string;
  item?: Workload;
}
export function getWorkloadBasicTableItem({
  link,
  name,
  item,
}: GetWorkloadBasicTableItemProps): WorkloadBasicTableItem {
  if (item) {
    const selfLink = linksOf(item).self!;

    return {
      ...item,
      selfLink: selfLink,
    };
  }

  if (link) {
    const parsed = parseLink(link, { useInputCtx: true });
    const links: { rel: string; href: string }[] = [{ rel: "self", href: link }];
    if (parsed.gvc) {
      links.push({ rel: "gvc", href: `/org/${parsed.org}/gvc/${parsed.gvc}` });
    }

    return {
      kind: "workload",
      name: parsed.name,
      selfLink: link,
      links: links,
      created: "",
      description: "",
      id: "",
      spec: { containers: [], supportDynamicTags: false, type: "standard" },
      lastModified: "",
      tags: {},
      version: 1,
    };
  }

  // assume name
  return {
    kind: "workload",
    name: name!,
    selfLink: `TODO test if this breaks`,
    links: [],
    created: "",
    description: "",
    id: "",
    spec: { containers: [], supportDynamicTags: false, type: "standard" },
    lastModified: "",
    tags: {},
    version: 1,
  };
}

export interface WorkloadTableItem extends Workload {
  type: string;
  readiness: string;
  suspended: "suspended" | "partiallySuspended" | "notSuspended";
  readyReplicas?: number;
  totalReplicas?: number;
  readyLocations?: number;
  totalLocations?: number;
  capacityAi?: boolean;
  metric?: OptionsMetric | "multi";
  minScale?: number;
  maxScale?: number;
  gvc: string;
  selfLink: string;
}

interface GetWorkloadTableItemProps {
  link?: string;
  name?: string;
  item?: Workload;
}
export function getWorkloadTableItem({
  link,
  name,
  item,
}: GetWorkloadTableItemProps): WorkloadTableItem {
  if (item) {
    const selfLink = linksOf(item).self!;
    const suspended = item.status?.suspendedStatus || workloadHelpers.getSuspendType(item);
    const capacityAi = workloadHelpers.getCapacityAi(item);
    const metric = workloadHelpers.getMetric(item.spec.defaultOptions);
    const minScale = workloadHelpers.getMinScale(item);
    const maxScale = workloadHelpers.getMaxScale(item);
    const gvc = selfLink.split("/")[4];
    const readiness = parseReadiness(item.health?.readiness);

    return {
      ...item,
      selfLink: selfLink,
      type: item.spec!.type,
      readiness: readiness === "notReady" ? "Not Ready" : "Ready",
      gvc: gvc,
      suspended: suspended,
      capacityAi: capacityAi,
      metric: metric,
      minScale: minScale,
      maxScale: maxScale,
      readyReplicas: item.health?.readyReplicas,
      totalReplicas: item.health?.totalReplicas,
      readyLocations: item.health?.readyLocations,
      totalLocations: item.health?.totalLocations,
    };
  }

  if (link) {
    const { name } = parseLink(link, { useInputCtx: true });

    return {
      kind: "workload",
      name: name,
      selfLink: link,
      links: [{ rel: "self", href: link }],
      created: "",
      description: "",
      id: "",
      spec: { containers: [], supportDynamicTags: false, type: "standard" },
      gvc: "",
      readiness: "Not Ready",
      suspended: "notSuspended",
      type: "standard",
      lastModified: "",
      tags: {},
      version: 1,
    };
  }

  // assume name
  return {
    kind: "workload",
    name: name!,
    selfLink: `TODO test if this breaks`,
    links: [],
    created: "",
    description: "",
    id: "",
    spec: { containers: [], supportDynamicTags: false, type: "standard" },
    gvc: "",
    readiness: "Not Ready",
    suspended: "notSuspended",
    type: "standard",
    lastModified: "",
    tags: {},
    version: 1,
  };
}

export const STORAGE_KEY_WORKLOAD_LIST_LIMIT_TO_CONTEXT_GVC = "STORAGE_KEY_WORKLOAD_LIST_LIMIT_TO_CONTEXT_GVC";
